import { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Play, Pause, BarChart2, Target, 
  ChevronDown, ChevronRight, ArrowRight, TrendingUp, Users, MousePointer
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { adminApi } from '@/lib/api';

const AdminABTests = () => {
  const [tests, setTests] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [testResults, setTestResults] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    page_id: '',
    element_type: 'hero',
    element_id: '',
    variants: [
      { id: 'control', name: 'Control', content: {}, traffic_percent: 50 },
      { id: 'variant-a', name: 'Variant A', content: {}, traffic_percent: 50 }
    ],
    status: 'draft',
    start_date: '',
    end_date: '',
    goal_metric: 'clicks'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [testsRes, pagesRes] = await Promise.all([
        adminApi.getABTests(),
        adminApi.getPages()
      ]);
      setTests(testsRes.data || []);
      setPages(pagesRes.data || []);
    } catch (error) {
      toast.error('Failed to load A/B tests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.page_id) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      if (editingTest) {
        await adminApi.updateABTest(editingTest.id, formData);
        toast.success('A/B test updated');
      } else {
        await adminApi.createABTest(formData);
        toast.success('A/B test created');
      }
      setModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save A/B test');
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.deleteABTest(id);
      toast.success('A/B test deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete A/B test');
    }
  };

  const handleStartTest = async (id) => {
    try {
      await adminApi.startABTest(id);
      toast.success('A/B test started');
      fetchData();
    } catch (error) {
      toast.error('Failed to start A/B test');
    }
  };

  const handleStopTest = async (id) => {
    try {
      await adminApi.stopABTest(id);
      toast.success('A/B test stopped');
      fetchData();
    } catch (error) {
      toast.error('Failed to stop A/B test');
    }
  };

  const viewResults = async (test) => {
    setSelectedTest(test);
    try {
      const res = await adminApi.getABTestResults(test.id);
      setTestResults(res.data);
      setResultsModalOpen(true);
    } catch (error) {
      toast.error('Failed to load results');
    }
  };

  const resetForm = () => {
    setEditingTest(null);
    setFormData({
      name: '',
      page_id: '',
      element_type: 'hero',
      element_id: '',
      variants: [
        { id: 'control', name: 'Control', content: {}, traffic_percent: 50 },
        { id: 'variant-a', name: 'Variant A', content: {}, traffic_percent: 50 }
      ],
      status: 'draft',
      start_date: '',
      end_date: '',
      goal_metric: 'clicks'
    });
  };

  const openEdit = (test) => {
    setEditingTest(test);
    setFormData({
      name: test.name,
      page_id: test.page_id,
      element_type: test.element_type,
      element_id: test.element_id || '',
      variants: test.variants || [
        { id: 'control', name: 'Control', content: {}, traffic_percent: 50 },
        { id: 'variant-a', name: 'Variant A', content: {}, traffic_percent: 50 }
      ],
      status: test.status,
      start_date: test.start_date || '',
      end_date: test.end_date || '',
      goal_metric: test.goal_metric || 'clicks'
    });
    setModalOpen(true);
  };

  const addVariant = () => {
    const newId = `variant-${String.fromCharCode(65 + formData.variants.length - 1)}`.toLowerCase();
    const newVariant = {
      id: newId,
      name: `Variant ${String.fromCharCode(65 + formData.variants.length - 1)}`,
      content: {},
      traffic_percent: 0
    };
    setFormData({ ...formData, variants: [...formData.variants, newVariant] });
  };

  const removeVariant = (index) => {
    if (formData.variants.length > 2) {
      const newVariants = formData.variants.filter((_, i) => i !== index);
      setFormData({ ...formData, variants: newVariants });
    }
  };

  const updateVariant = (index, field, value) => {
    const newVariants = [...formData.variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setFormData({ ...formData, variants: newVariants });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'running':
        return <Badge className="bg-green-500">Running</Badge>;
      case 'paused':
        return <Badge className="bg-yellow-500">Paused</Badge>;
      case 'completed':
        return <Badge className="bg-blue-500">Completed</Badge>;
      default:
        return <Badge variant="secondary">Draft</Badge>;
    }
  };

  const getPageName = (pageId) => {
    const page = pages.find(p => p.id === pageId);
    return page?.title || 'Unknown Page';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-ab-tests">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">A/B Testing</h1>
          <p className="text-muted-foreground">Create and manage A/B tests to optimize content performance</p>
        </div>
        <Button onClick={() => { resetForm(); setModalOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" /> Create Test
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{tests.length}</p>
                <p className="text-sm text-muted-foreground">Total Tests</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <Play className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{tests.filter(t => t.status === 'running').length}</p>
                <p className="text-sm text-muted-foreground">Running</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{tests.filter(t => t.status === 'completed').length}</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Pause className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{tests.filter(t => t.status === 'draft').length}</p>
                <p className="text-sm text-muted-foreground">Drafts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tests List */}
      {tests.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No A/B Tests Yet</h3>
            <p className="text-muted-foreground mb-4">Create your first A/B test to start optimizing your content</p>
            <Button onClick={() => { resetForm(); setModalOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" /> Create Test
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {tests.map((test) => (
            <Card key={test.id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{test.name}</h3>
                      {getStatusBadge(test.status)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>Page: {getPageName(test.page_id)}</span>
                      <span>Element: {test.element_type}</span>
                      <span>Goal: {test.goal_metric}</span>
                      <span>Variants: {test.variants?.length || 0}</span>
                    </div>
                    
                    {test.variants && test.variants.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {test.variants.map((variant, index) => (
                          <div key={variant.id} className="flex items-center gap-3">
                            <span className="text-sm font-medium w-24">{variant.name}</span>
                            <Progress value={variant.traffic_percent} className="flex-1 h-2" />
                            <span className="text-sm text-muted-foreground w-12">{variant.traffic_percent}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {test.status === 'draft' && (
                      <Button variant="outline" size="sm" onClick={() => handleStartTest(test.id)}>
                        <Play className="w-4 h-4 mr-1" /> Start
                      </Button>
                    )}
                    {test.status === 'running' && (
                      <Button variant="outline" size="sm" onClick={() => handleStopTest(test.id)}>
                        <Pause className="w-4 h-4 mr-1" /> Stop
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => viewResults(test)}>
                      <BarChart2 className="w-4 h-4 mr-1" /> Results
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(test)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete A/B Test</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{test.name}"? This will also delete all test results.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(test.id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTest ? 'Edit A/B Test' : 'Create A/B Test'}</DialogTitle>
            <DialogDescription>Set up your A/B test to compare different content variations</DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="basic">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="variants">Variants</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Test Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Homepage Hero CTA Test"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Page *</Label>
                <Select
                  value={formData.page_id}
                  onValueChange={(v) => setFormData({ ...formData, page_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a page" />
                  </SelectTrigger>
                  <SelectContent>
                    {pages.map((page) => (
                      <SelectItem key={page.id} value={page.id}>{page.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Element Type</Label>
                  <Select
                    value={formData.element_type}
                    onValueChange={(v) => setFormData({ ...formData, element_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hero">Hero Section</SelectItem>
                      <SelectItem value="cta">CTA Button</SelectItem>
                      <SelectItem value="headline">Headline</SelectItem>
                      <SelectItem value="section">Full Section</SelectItem>
                      <SelectItem value="button">Button</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Goal Metric</Label>
                  <Select
                    value={formData.goal_metric}
                    onValueChange={(v) => setFormData({ ...formData, goal_metric: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clicks">Clicks</SelectItem>
                      <SelectItem value="conversions">Conversions</SelectItem>
                      <SelectItem value="time_on_page">Time on Page</SelectItem>
                      <SelectItem value="scroll_depth">Scroll Depth</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="variants" className="space-y-4 pt-4">
              <div className="flex justify-between items-center">
                <Label>Test Variants</Label>
                <Button variant="outline" size="sm" onClick={addVariant}>
                  <Plus className="w-4 h-4 mr-1" /> Add Variant
                </Button>
              </div>
              
              {formData.variants.map((variant, index) => (
                <Card key={index}>
                  <CardContent className="pt-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">{index === 0 ? 'Control (Original)' : `Variant ${String.fromCharCode(64 + index)}`}</h4>
                      {index > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeVariant(index)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Variant Name</Label>
                        <Input
                          value={variant.name}
                          onChange={(e) => updateVariant(index, 'name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Traffic %</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={variant.traffic_percent}
                          onChange={(e) => updateVariant(index, 'traffic_percent', parseInt(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                    
                    {formData.element_type === 'headline' && (
                      <div className="space-y-2">
                        <Label>Headline Text</Label>
                        <Input
                          value={variant.content?.headline || ''}
                          onChange={(e) => updateVariant(index, 'content', { ...variant.content, headline: e.target.value })}
                          placeholder="Enter headline text"
                        />
                      </div>
                    )}
                    
                    {formData.element_type === 'cta' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Button Text</Label>
                          <Input
                            value={variant.content?.button_text || ''}
                            onChange={(e) => updateVariant(index, 'content', { ...variant.content, button_text: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Button Color</Label>
                          <Input
                            type="color"
                            value={variant.content?.button_color || '#1570B5'}
                            onChange={(e) => updateVariant(index, 'content', { ...variant.content, button_color: e.target.value })}
                          />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              
              <p className="text-sm text-muted-foreground">
                Total traffic: {formData.variants.reduce((sum, v) => sum + (v.traffic_percent || 0), 0)}%
                {formData.variants.reduce((sum, v) => sum + (v.traffic_percent || 0), 0) !== 100 && (
                  <span className="text-red-500 ml-2">(Should equal 100%)</span>
                )}
              </p>
            </TabsContent>
            
            <TabsContent value="settings" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date (Optional)</Label>
                  <Input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date (Optional)</Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Element ID (Optional)</Label>
                <Input
                  value={formData.element_id}
                  onChange={(e) => setFormData({ ...formData, element_id: e.target.value })}
                  placeholder="Specific element ID to test"
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to test all elements of the selected type
                </p>
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>{editingTest ? 'Update' : 'Create'} Test</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Results Modal */}
      <Dialog open={resultsModalOpen} onOpenChange={setResultsModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Test Results: {selectedTest?.name}</DialogTitle>
            <DialogDescription>Performance data for each variant</DialogDescription>
          </DialogHeader>
          
          {testResults ? (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <Users className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-2xl font-bold">{testResults.total_impressions || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Impressions</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <MousePointer className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-2xl font-bold">{testResults.total_clicks || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Clicks</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <TrendingUp className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-2xl font-bold">{testResults.total_conversions || 0}</p>
                    <p className="text-sm text-muted-foreground">Conversions</p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-medium">Variant Performance</h4>
                {testResults.variant_results?.map((result, index) => (
                  <Card key={index} className={result.is_winner ? 'border-green-500 border-2' : ''}>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{result.variant_name}</span>
                          {result.is_winner && <Badge className="bg-green-500">Winner</Badge>}
                        </div>
                        <span className="text-sm text-muted-foreground">{result.traffic_percent}% traffic</span>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-xl font-bold">{result.impressions || 0}</p>
                          <p className="text-xs text-muted-foreground">Impressions</p>
                        </div>
                        <div>
                          <p className="text-xl font-bold">{result.clicks || 0}</p>
                          <p className="text-xs text-muted-foreground">Clicks</p>
                        </div>
                        <div>
                          <p className="text-xl font-bold">{result.conversions || 0}</p>
                          <p className="text-xs text-muted-foreground">Conversions</p>
                        </div>
                        <div>
                          <p className="text-xl font-bold">{(result.conversion_rate || 0).toFixed(2)}%</p>
                          <p className="text-xs text-muted-foreground">Conv. Rate</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {(!testResults.variant_results || testResults.variant_results.length === 0) && (
                <p className="text-center text-muted-foreground py-8">
                  No results yet. Start the test to begin collecting data.
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setResultsModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminABTests;
